import React, { useState, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { useDispatch } from "react-redux";
import { fetchClinicianById } from "../../store/thunks/cliniciansThunk";
import { fetchAppointmentsByClinicianId } from "../../store/thunks/appointmentsThunk";
import {
  fetchClientsByClinicianId,
  fetchSupervisingClientsByClinicianId,
} from "../../store/thunks/clientsThunk";
import { fetchBlocksByClinicianId } from "../../store/thunks/blocksThunk";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import { setClient } from "../../store/slices/clientsSlice";

import Table from "../../applicationUi/components/Table";
import StatusLights from "../../applicationUi/components/StatusLights";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import ClientModal from "../../modals/ClientModal";
import ModifyBlockModal from "../../modals/ModifyBlockModal";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ClinicianDetailsModal({ clinicianId, onClose }) {
  const dispatch = useDispatch();

  // Basic state for modal data and tab selection
  const [currentTab, setCurrentTab] = useState("appointments");
  const [clinician, setClinician] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [supervisingClients, setSupervisingClients] = useState([]);
  const [blocks, setBlocks] = useState([]);

  const [appointmentFilter, setAppointmentFilter] = useState("All");
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] =
    useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState(null);

  const [appointmentSearchQuery, setAppointmentSearchQuery] = useState("");
  const [clientSearchQuery, setClientSearchQuery] = useState(""); // Custom search query
  const [supervisingSearchQuery, setSupervisingSearchQuery] = useState("");

  // --- Frontend Pagination & Sorting State ---
  const itemsPerPage = 10;

  // Appointments
  const [appointmentsSortConfig, setAppointmentsSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentAppointmentsPage, setCurrentAppointmentsPage] = useState(1);

  // Treating Clients
  const [clientsSortConfig, setClientsSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentClientsPage, setCurrentClientsPage] = useState(1);

  // Supervising Clients
  const [supervisingSortConfig, setSupervisingSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentSupervisingPage, setCurrentSupervisingPage] = useState(1);

  // Blocks
  const [blocksSortConfig, setBlocksSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentBlocksPage, setCurrentBlocksPage] = useState(1);

  const getFilteredAppointments = () => {
    if (!appointmentSearchQuery) return [...appointments]; // Show all when search is empty

    const query = appointmentSearchQuery.toLowerCase();

    return appointments.filter((appointment) => {
      const clientName = appointment.client
        ? `${appointment.client.firstName} ${appointment.client.lastName}`
        : "Unknown";

      const serviceDescription = appointment.service?.description || "";

      const appointmentStatus = Array.isArray(appointment.status)
        ? appointment.status.join(" ").toLowerCase() // Convert array to a string
        : "";

      return (
        (appointment.title &&
          appointment.title.toLowerCase().includes(query)) ||
        (serviceDescription &&
          serviceDescription.toLowerCase().includes(query)) ||
        (clientName && clientName.toLowerCase().includes(query)) ||
        (appointmentStatus && appointmentStatus.includes(query))
      );
    });
  };

  const getFilteredClients = () => {
    // First filter out discharged clients, then apply search
    const activeClients = clients.filter(
      (client) => client.clientStatus !== "discharged"
    );

    if (!clientSearchQuery) return activeClients; // Show all active clients when search is empty

    const query = clientSearchQuery.toLowerCase();
    return activeClients.filter((client) => {
      return (
        client.firstName.toLowerCase().includes(query) ||
        client.lastName.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query)
      );
    });
  };

  const getFilteredSupervisingClients = () => {
    // First filter out discharged clients, then apply search
    const activeSupervisingClients = supervisingClients.filter(
      (client) => client.clientStatus !== "discharged"
    );

    if (!supervisingSearchQuery) return activeSupervisingClients; // Show all active clients when search is empty

    const query = supervisingSearchQuery.toLowerCase();
    return activeSupervisingClients.filter((client) => {
      return (
        client.firstName.toLowerCase().includes(query) ||
        client.lastName.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query)
      );
    });
  };

  // --- Helper Functions for Sorting & Pagination ---
  const getNestedValue = (obj, key) => {
    return key.split(".").reduce((o, i) => (o ? o[i] : undefined), obj);
  };

  const sortData = (data, sortConfig) => {
    if (!sortConfig.key) return data;
    const sortedData = [...data].sort((a, b) => {
      let aVal = getNestedValue(a, sortConfig.key);
      let bVal = getNestedValue(b, sortConfig.key);

      // If the key represents a date field, compare as Date objects
      if (sortConfig.key === "start" || sortConfig.key === "end") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
    return sortedData;
  };

  const paginateData = (data, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  // --- Data Fetching ---
  useEffect(() => {
    if (clinicianId) {
      dispatch(fetchClinicianById(clinicianId))
        .unwrap()
        .then((data) => {
          setClinician(data);
          return Promise.all([
            dispatch(fetchAppointmentsByClinicianId(data._id)).unwrap(),
            dispatch(fetchClientsByClinicianId(data._id)).unwrap(),
            dispatch(fetchSupervisingClientsByClinicianId(data._id)).unwrap(),
            dispatch(fetchBlocksByClinicianId(data._id)).unwrap(),
          ]);
        })
        .then(
          ([
            appointmentsData,
            clientsData,
            supervisingClientsData,
            blocksData,
          ]) => {
            setAppointments(
              Array.isArray(appointmentsData) ? appointmentsData : []
            );
            setClients(Array.isArray(clientsData) ? clientsData : []);
            setSupervisingClients(
              Array.isArray(supervisingClientsData)
                ? supervisingClientsData
                : []
            );
            setBlocks(Array.isArray(blocksData) ? blocksData : []);
          }
        )
        .catch((error) =>
          console.error("Error fetching clinician data:", error)
        );
    }
  }, [clinicianId, dispatch]);

  // --- Tab Definitions ---
  const tabs = [
    { name: `Appointments - ${appointments.length}`, key: "appointments" },
    {
      name: `Treating Clients - ${
        clients.filter((client) => client.clientStatus !== "discharged").length
      }`,
      key: "clients",
    },
    {
      name: `Supervising Clients - ${
        supervisingClients.filter(
          (client) => client.clientStatus !== "discharged"
        ).length
      }`,
      key: "supervising-clients",
    },
    { name: `Blocks - ${blocks.length}`, key: "blocks" },
  ];

  // --- Data Transformations & Filtering ---

  // For Appointments: filter by date and add a clientName field
  // For Appointments: Apply search and date filtering together
  const now = new Date();
  const filteredAppointments = getFilteredAppointments().filter(
    (appointment) => {
      const appointmentDate = new Date(appointment.start);
      if (appointmentFilter === "Future") return appointmentDate >= now;
      if (appointmentFilter === "Past") return appointmentDate < now;
      return true;
    }
  );

  // Sort the results
  const sortedAppointments = sortData(
    filteredAppointments.map((appt) => ({
      ...appt,
      clientName:
        appt.client && appt.client.firstName && appt.client.lastName
          ? `${appt.client.firstName} ${appt.client.lastName}`
          : "Unknown",
    })),
    appointmentsSortConfig
  );

  // Pagination
  const totalAppointmentsPages = Math.ceil(
    sortedAppointments.length / itemsPerPage
  );
  const paginatedAppointments = paginateData(
    sortedAppointments,
    currentAppointmentsPage,
    itemsPerPage
  );

  // For Clients (treating)
  const filteredClients = getFilteredClients();
  const sortedClients = sortData(filteredClients, clientsSortConfig);
  const totalClientsPages = Math.ceil(sortedClients.length / itemsPerPage);
  const paginatedClients = paginateData(
    sortedClients,
    currentClientsPage,
    itemsPerPage
  );

  // For Supervising Clients
  const filteredSupervisingClients = getFilteredSupervisingClients();
  const sortedSupervisingClients = sortData(
    filteredSupervisingClients,
    supervisingSortConfig
  );

  const totalSupervisingPages = Math.ceil(
    sortedSupervisingClients.length / itemsPerPage
  );

  const paginatedSupervisingClients = paginateData(
    sortedSupervisingClients,
    currentSupervisingPage,
    itemsPerPage
  );

  const transformedAppointments = filteredAppointments.map((appt) => ({
    ...appt,
    clientName:
      appt.client && appt.client.firstName && appt.client.lastName
        ? `${appt.client.firstName} ${appt.client.lastName}`
        : "Unknown",
  }));

  // For Blocks, add computed fields to enable sorting
  const transformedBlocks = blocks.map((block) => ({
    ...block,
    clientName:
      block.appointments && block.appointments.length > 0
        ? `${block.appointments[0]?.client?.firstName || "Unknown"} ${
            block.appointments[0]?.client?.lastName || ""
          }`
        : "No Client",
    start:
      block.appointments && block.appointments.length > 0
        ? block.appointments[0].start
        : null,
    end:
      block.appointments && block.appointments.length > 0
        ? block.appointments[block.appointments.length - 1].start
        : null,
    appointmentsCount: block.appointments ? block.appointments.length : 0,
  }));
  const sortedBlocks = sortData(transformedBlocks, blocksSortConfig);
  const totalBlocksPages = Math.ceil(sortedBlocks.length / itemsPerPage);
  const paginatedBlocks = paginateData(
    sortedBlocks,
    currentBlocksPage,
    itemsPerPage
  );

  // --- Sorting Handlers ---
  const handleAppointmentsSortChange = (key) => {
    let direction = "ascending";
    if (
      appointmentsSortConfig.key === key &&
      appointmentsSortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setAppointmentsSortConfig({ key, direction });
  };

  const handleClientsSortChange = (key) => {
    let direction = "ascending";
    if (
      clientsSortConfig.key === key &&
      clientsSortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setClientsSortConfig({ key, direction });
  };

  const handleSupervisingSortChange = (key) => {
    let direction = "ascending";
    if (
      supervisingSortConfig.key === key &&
      supervisingSortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSupervisingSortConfig({ key, direction });
  };

  const handleBlocksSortChange = (key) => {
    let direction = "ascending";
    if (
      blocksSortConfig.key === key &&
      blocksSortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setBlocksSortConfig({ key, direction });
  };

  // --- Pagination Handlers ---

  const handleAppointmentsPageChange = (page) => {
    setCurrentAppointmentsPage(page);
  };

  const handleClientsPageChange = (page) => {
    setCurrentClientsPage(page);
  };

  const handleSupervisingPageChange = (page) => {
    setCurrentSupervisingPage(page);
  };

  const handleBlocksPageChange = (page) => {
    setCurrentBlocksPage(page);
  };

  // --- Row Click Handlers ---
  const handleAppointmentClick = (appointmentId) => {
    const appointment = appointments.find((appt) => appt._id === appointmentId);
    dispatch(setSelectedAppointment(appointment));
    setIsAppointmentSummaryOpen(true);
  };

  const handleClientClick = (clientId) => {
    const client = clients.find((client) => client._id === clientId);
    dispatch(setClient(client));
    setIsClientModalOpen(true);
  };

  const handleSupervisingClientClick = (clientId) => {
    const supervisingClient = supervisingClients.find(
      (client) => client._id === clientId
    );
    dispatch(setClient(supervisingClient));
    setIsClientModalOpen(true);
  };

  const handleBlockClick = (blockId) => {
    setSelectedBlockId(blockId);
    setIsBlockModalOpen(true);
  };

  const handleCloseAppointmentSummary = () => {
    dispatch(setSelectedAppointment(null));
    setIsAppointmentSummaryOpen(false);
  };

  const handleCloseClientModal = () => {
    dispatch(setClient(null));
    setIsClientModalOpen(false);
  };

  const handleCloseBlockModal = () => {
    setSelectedBlockId(null);
    setIsBlockModalOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50">
      <div className="relative mx-4 my-8 w-full max-w-6xl rounded-sm bg-white border border-gray-200 shadow-xl">
        {/* Header & Close Button */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {clinician?.firstName} {clinician?.lastName}
            </h3>
            <p className="text-sm text-gray-600 mt-1">Clinician Details</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-4">
          {/* Mobile Dropdown */}
          <div className="sm:hidden">
            <select
              value={currentTab}
              onChange={(e) => setCurrentTab(e.target.value)}
              className="w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            >
              {tabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.name}
                </option>
              ))}
            </select>
          </div>
          {/* Desktop Tabs */}
          <div className="hidden sm:block">
            <nav className="flex space-x-8 border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setCurrentTab(tab.key)}
                  className={classNames(
                    currentTab === tab.key
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                    "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
          {currentTab === "appointments" && (
            <div className="space-y-4">
              {/* Search and Filter Controls */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <input
                    id="appointment-search"
                    type="text"
                    placeholder="Search appointments..."
                    value={appointmentSearchQuery}
                    onChange={(e) => setAppointmentSearchQuery(e.target.value)}
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="w-full sm:w-48">
                  <select
                    value={appointmentFilter}
                    onChange={(e) => setAppointmentFilter(e.target.value)}
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="All">All Appointments</option>
                    <option value="Future">Future</option>
                    <option value="Past">Past</option>
                  </select>
                </div>
              </div>

              <Table
                title="Appointments"
                description="List of appointments"
                columns={[
                  { header: "Title", accessor: "title", sortable: true },
                  { header: "Client", accessor: "clientName", sortable: true },
                  { header: "Service Date", accessor: "start", sortable: true },
                  {
                    header: "Status",
                    accessor: (row) => <StatusLights appointment={row} />,
                    sortable: false,
                  },
                ]}
                data={paginatedAppointments}
                onRowClick={handleAppointmentClick}
                showPagination={true}
                currentPage={currentAppointmentsPage}
                totalPages={totalAppointmentsPages}
                onPageChange={handleAppointmentsPageChange}
                onSortChange={handleAppointmentsSortChange}
                totalResults={sortedAppointments.length}
                sortConfig={appointmentsSortConfig}
              />
            </div>
          )}

          {currentTab === "clients" && (
            <div className="space-y-4">
              <div>
                <input
                  id="client-search"
                  type="text"
                  placeholder="Search treating clients..."
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <Table
                title="Treating Clients"
                description="List of active treating clients"
                columns={[
                  { header: "First Name", accessor: "firstName", sortable: true },
                  { header: "Last Name", accessor: "lastName", sortable: true },
                  { header: "Email", accessor: "email", sortable: true },
                  {
                    header: "Phone",
                    accessor: "phoneNumbers[0].number",
                    sortable: true,
                  },
                ]}
                data={paginatedClients}
                onRowClick={handleClientClick}
                showPagination={true}
                currentPage={currentClientsPage}
                totalPages={totalClientsPages}
                onPageChange={handleClientsPageChange}
                onSortChange={handleClientsSortChange}
                totalResults={sortedClients.length}
                sortConfig={clientsSortConfig}
              />
            </div>
          )}

          {currentTab === "supervising-clients" && (
            <div className="space-y-4">
              <div>
                <input
                  id="supervising-client-search"
                  type="text"
                  placeholder="Search supervising clients..."
                  value={supervisingSearchQuery}
                  onChange={(e) => setSupervisingSearchQuery(e.target.value)}
                  className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <Table
                title="Supervising Clients"
                description="List of active supervising clients"
                columns={[
                  { header: "First Name", accessor: "firstName", sortable: true },
                  { header: "Last Name", accessor: "lastName", sortable: true },
                  { header: "Email", accessor: "email", sortable: true },
                  {
                    header: "Phone",
                    accessor: "phoneNumbers[0].number",
                    sortable: true,
                  },
                ]}
                data={paginatedSupervisingClients}
                onRowClick={handleSupervisingClientClick}
                showPagination={true}
                currentPage={currentSupervisingPage}
                totalPages={totalSupervisingPages}
                onPageChange={handleSupervisingPageChange}
                onSortChange={handleSupervisingSortChange}
                totalResults={sortedSupervisingClients.length}
                sortConfig={supervisingSortConfig}
              />
            </div>
          )}

          {currentTab === "blocks" && (
            <div>
              <Table
                title="Blocks"
                description="List of blocks"
                columns={[
                  { header: "Client", accessor: "clientName", sortable: false },
                  { header: "Starts On", accessor: "start", sortable: true },
                  { header: "Ends On", accessor: "end", sortable: true },
                  {
                    header: "Appointments",
                    accessor: "appointmentsCount",
                    sortable: true,
                  },
                ]}
                data={paginatedBlocks}
                onRowClick={handleBlockClick}
                showPagination={true}
                currentPage={currentBlocksPage}
                totalPages={totalBlocksPages}
                onPageChange={handleBlocksPageChange}
                onSortChange={handleBlocksSortChange}
                totalResults={sortedBlocks.length}
                sortConfig={blocksSortConfig}
              />
            </div>
          )}
        </div>

        {isAppointmentSummaryOpen && (
          <AppointmentSummary
            open={isAppointmentSummaryOpen}
            onClose={handleCloseAppointmentSummary}
            onReschedule={(appt) => console.log("Reschedule:", appt)}
          />
        )}

        {isClientModalOpen && (
          <ClientModal
            isOpen={isClientModalOpen}
            onClose={handleCloseClientModal}
          />
        )}

        {isBlockModalOpen && (
          <ModifyBlockModal
            isOpen={isBlockModalOpen}
            onClose={handleCloseBlockModal}
            blockId={selectedBlockId}
          />
        )}
      </div>
    </div>
  );
}
